// Liegt hinter diesem Schalter eine eingelesene Datei — und welche?
//
// ⚠️ Toms Frage im Wortlaut: er sieht vier Schalter und weiß nicht, ob hinter einem davon
// überhaupt etwas liegt. Die Fassungsliste unter dem Formular beantwortet das für das WERK, nicht
// für den einzelnen Schalter; dazwischen musste er selbst übersetzen (`528hz-frequenz` → welcher
// Kasten?). Genau diese Übersetzung macht jetzt das Backend (`services/fassungsSlot.ts`, Option 1b
// der Analyse) und liefert sie als `slot` im DTO mit. Hier wird sie nur angezeigt.
//
// ── Warum diese Zeile NICHT sperrt (UX-Frage 1 der Analyse § 5) ────────────────────────────────
// Sie graut nichts aus und verbirgt nichts. Der Grund ist gemessen und nicht Geschmack: die
// Schalter steuern AUCH Werke der alten Welt, deren Adressen im Formular stehen und deren Dateien
// nie durch den Ingest liefen (Analyse § 2 — fünf veröffentlichte Werke, `tracks`-Rows intakt,
// Adressen auf `ee-cms`). Ein ausgegrauter Schalter „ohne eingelesene Datei" würde dort eine
// funktionierende Fläche sperren, und zwar auf Grundlage einer Quelle, die für dieses Werk gar
// nicht zuständig ist. Verbergen wäre noch schlechter: dann verschwände die Redaktionsfähigkeit
// ohne jede Spur.
//
// Deshalb: diese Zeile ist AUSKUNFT, nie Verbot. Sie sagt, was das Speicher-Feld trägt — und der
// Mensch entscheidet.
//
// ── Und warum bei einem Werk ganz ohne Ingest gar nichts steht ─────────────────────────────────
// Trägt ein Werk NULL eingelesene Dateien, wird hier nichts gerendert (`nichtsBekannt`). Vier
// Kästen mit „keine eingelesene Datei" an einem tadellos funktionierenden Alt-Werk sähen aus wie
// vier Defekte. Den einen wahren Satz dazu sagt die Sektionsüberschrift, einmal.
import React from 'react';
import { formatiereDateigroesse } from '../../utils/dateigroesse';
import { GRUNDFASSUNG, LIEFERUNG_ANZEIGE, SLOT_ZU_DTO } from '../../types/fassungen';
import type { FassungsEintrag } from '../../types/fassungen';
import type { VariantSlot } from '../../types/audio';

interface SchalterDeckungProps {
  /** Der Schalter, an dem diese Zeile steht — in der Schreibweise des Formulars. */
  slot: VariantSlot;
  /** Alle Einträge des Werks. `null`, solange nichts geladen ist. */
  eintraege: FassungsEintrag[] | null;
  laedt: boolean;
  /** `true`, wenn der Abruf fehlschlug — dann wird KEINE Aussage über Dateien gemacht. */
  fehlgeschlagen: boolean;
}

export const SchalterDeckung: React.FC<SchalterDeckungProps> = ({
  slot,
  eintraege,
  laedt,
  fehlgeschlagen,
}) => {
  if (laedt) {
    return <div className="skeleton h-5 w-56" aria-hidden="true"></div>;
  }

  // ⚠️ Auf dem Fehlerpfad wird hier NICHTS behauptet — weder „Datei da" noch „keine Datei". Den
  // Grund nennt die Sektionsüberschrift einmal, mit Wiederholen-Knopf. Vier Fehlermeldungen an
  // vier Schaltern wären dieselbe Aussage viermal.
  if (fehlgeschlagen || !eintraege) return null;

  // Kein Ingest-Lauf: schweigen. Begründung im Dateikopf.
  const nichtsBekannt = eintraege.length === 0;
  if (nichtsBekannt) return null;

  const treffer = eintraege.filter((e) => e.slot === SLOT_ZU_DTO[slot]);

  if (treffer.length === 0) {
    return (
      <p className="text-sm text-secondary-500">
        Keine eingelesene Datei für diesen Schalter.
      </p>
    );
  }

  return (
    <div className="space-y-1">
      {/* ⚠️ Zwei Dateien an einem Schalter sind kein Detail, sondern der teuerste Fall: die
          Oberfläche kann nur eine zeigen, der Haken sieht vollständig aus, und eine Fassung
          bleibt unsichtbar. Das Backend meldet es zusätzlich in `hinweise`; hier steht es
          nochmals AN DER STELLE, weil die Sektionsüberschrift beim Blick auf den Kasten längst
          weggescrollt ist. */}
      {treffer.length > 1 && (
        <p className="text-sm font-medium text-warning-700">
          {treffer.length} eingelesene Dateien an diesem Schalter — die Oberfläche kann nur eine
          davon führen.
        </p>
      )}
      <ul className="space-y-1">
        {treffer.map((e) => (
          /* ⚠️ `break-words` ist kein Schönheitszusatz: Dateinamen wie
             `audio-naturklang-meeres-sound.flac` sind für den Umbruch EIN Wort, und ohne diese
             Erlaubnis schoben sie die Zeile über die Fensterbreite hinaus — 💎 hat bei 640 px
             Fenster 696 px Inhalt gemessen, also Querscroll auf einer Formularseite. */
          <li key={e.pfad} className="break-words text-sm text-secondary-600">
            <span className="font-medium text-secondary-800">{e.fassung ?? GRUNDFASSUNG}</span>
            {' · '}
            {e.dateiname}
            {e.groesseBytes !== null && ` · ${formatiereDateigroesse(e.groesseBytes)}`}
            {/* ⚠️ Zugriff und Lieferart stehen hier, weil sie beim Zusammenlegen der drei Flächen
                sonst STILL verschwunden wären: sie standen in der eigenständigen Fassungsliste,
                die in diesen Zeilen aufgegangen ist. Aufgefallen ist es an knip („LIEFERUNG_ANZEIGE
                ohne Verbraucher") — ein Zusammenlegen, das nebenbei Information verliert, ist
                keine Vereinfachung, sondern ein Verlust mit besserer Optik.
                Angezeigt wird die WIRKUNG („nur mit Token"), nicht der Zonenname: der ist ein
                Betriebsname und steht als Titel daneben. */}
            {e.lieferung && ` · ${LIEFERUNG_ANZEIGE[e.lieferung] ?? e.lieferung}`}
            <span
              className={`badge ml-2 ${e.oeffentlich ? 'badge-secondary' : 'badge-primary'}`}
              title={e.zone ?? undefined}
            >
              {e.oeffentlich ? 'öffentlich' : 'nur mit Token'}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
